/**
 * AddHostModal - one-click create user + org + event + send magic link.
 */
import { useEffect, useState } from "react";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";

const headers = () => {
  const t = localStorage.getItem("session_token");
  return t ? { "Content-Type": "application/json", Authorization: `Bearer ${t}` } : { "Content-Type": "application/json" };
};

const CATS = ["makeup","hair","nails","lashes","culinary","wellness","events","travel","style","instructor","dispensary"];

export default function AddHostModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "makeup", org_id: "", new_org_name: "" });
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/admin/organizations`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setOrgs(Array.isArray(d) ? d : (d.organizations || [])))
      .catch(() => {});
  }, [open]);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/hosts/create-pipeline`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({
          display_name: form.name,
          email: form.email,
          phone: form.phone,
          primary_category: form.category,
          org_id: form.org_id || undefined,
          new_org_name: form.org_id ? undefined : form.new_org_name,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast.success(j.magic_link ? `Created. Magic link: ${j.magic_link}` : "Host created.");
      onCreated?.(j);
      onClose?.();
      setForm({ name: "", email: "", phone: "", category: "makeup", org_id: "", new_org_name: "" });
    } catch (e) {
      toast.error(`Create failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="add-host-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" />Add Host (full pipeline)</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Display Name *</Label>
            <Input className="h-9 text-xs mt-1" value={form.name} onChange={e => upd("name", e.target.value)} data-testid="add-host-name" />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input type="email" className="h-9 text-xs mt-1" value={form.email} onChange={e => upd("email", e.target.value)} data-testid="add-host-email" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input className="h-9 text-xs mt-1" value={form.phone} onChange={e => upd("phone", e.target.value)} data-testid="add-host-phone" />
          </div>
          <div>
            <Label className="text-xs">Primary Category</Label>
            <select className="w-full h-9 text-xs border border-gray-200 rounded-md px-2 bg-white mt-1 capitalize"
              value={form.category} onChange={e => upd("category", e.target.value)} data-testid="add-host-category">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Existing Organization</Label>
            <select className="w-full h-9 text-xs border border-gray-200 rounded-md px-2 bg-white mt-1"
              value={form.org_id} onChange={e => upd("org_id", e.target.value)} data-testid="add-host-org">
              <option value="">- create new -</option>
              {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.name || o.display_name || o.org_id}</option>)}
            </select>
          </div>
          {!form.org_id && (
            <div>
              <Label className="text-xs">New Organization Name</Label>
              <Input className="h-9 text-xs mt-1" value={form.new_org_name} onChange={e => upd("new_org_name", e.target.value)} placeholder="e.g. Mia Thomas Makeup" data-testid="add-host-new-org" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="add-host-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5 bg-black hover:bg-gray-800 text-white" data-testid="add-host-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}Create Host
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
